import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RoadmapItem } from "@/api/entities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Plus, 
  CheckCircle2, 
  Circle, 
  Clock, 
  Lightbulb, 
  Wrench, 
  Rocket, 
  Bug,
  Trash2
} from "lucide-react";

const categoriaConfig = {
  desarrollo: { label: "Desarrollo", icon: Rocket, color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300" },
  correccion: { label: "Corrección", icon: Bug, color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300" },
  mejora: { label: "Mejora", icon: Wrench, color: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300" },
  idea: { label: "Idea", icon: Lightbulb, color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300" }
};

const prioridadConfig = {
  alta: { label: "Alta", color: "bg-red-500" },
  media: { label: "Media", color: "bg-amber-500" },
  baja: { label: "Baja", color: "bg-green-500" }
};

export default function RoadmapPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState("todas");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    categoria: "desarrollo",
    prioridad: "media"
  });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["roadmap"],
    queryFn: () => RoadmapItem.list({}, { orderBy: 'created_date', orderDirection: 'desc' })
  });

  const createMutation = useMutation({
    mutationFn: (data) => RoadmapItem.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roadmap"] });
      setDialogOpen(false);
      setFormData({ titulo: "", descripcion: "", categoria: "desarrollo", prioridad: "media" });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => RoadmapItem.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["roadmap"] })
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => RoadmapItem.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["roadmap"] })
  });

  const toggleEstado = (item) => {
    const nuevoEstado = item.estado === "completada" ? "pendiente" : "completada";
    updateMutation.mutate({ id: item.id, data: { estado: nuevoEstado } });
  };

  const filteredItems = items.filter(item => {
    if (filtroCategoria !== "todas" && item.categoria !== filtroCategoria) return false;
    if (filtroEstado !== "todos" && item.estado !== filtroEstado) return false;
    return true;
  });

  const pendientes = filteredItems.filter(i => i.estado === "pendiente");
  const completadas = filteredItems.filter(i => i.estado === "completada");

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Hoja de Ruta</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Ideas, desarrollos y correcciones pendientes</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Nueva tarea
        </Button>
      </div>

      <div className="flex gap-4 mb-6">
        <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas</SelectItem>
            <SelectItem value="desarrollo">Desarrollo</SelectItem>
            <SelectItem value="correccion">Corrección</SelectItem>
            <SelectItem value="mejora">Mejora</SelectItem>
            <SelectItem value="idea">Idea</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="completada">Completadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : (
        <div className="space-y-6">
          {pendientes.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5" /> Pendientes ({pendientes.length})
              </h2>
              <div className="space-y-3">
                {pendientes.map(item => (
                  <RoadmapCard 
                    key={item.id} 
                    item={item} 
                    onToggle={toggleEstado}
                    onDelete={(id) => deleteMutation.mutate(id)}
                  />
                ))}
              </div>
            </div>
          )}

          {completadas.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" /> Completadas ({completadas.length})
              </h2>
              <div className="space-y-3 opacity-60">
                {completadas.map(item => (
                  <RoadmapCard 
                    key={item.id} 
                    item={item} 
                    onToggle={toggleEstado}
                    onDelete={(id) => deleteMutation.mutate(id)}
                  />
                ))}
              </div>
            </div>
          )}

          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No hay tareas en la hoja de ruta
            </div>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Nueva tarea</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Título"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
            />
            <Textarea
              placeholder="Descripción detallada..."
              rows={4}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            />
            <div className="flex gap-4">
              <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desarrollo">Desarrollo</SelectItem>
                  <SelectItem value="correccion">Corrección</SelectItem>
                  <SelectItem value="mejora">Mejora</SelectItem>
                  <SelectItem value="idea">Idea</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formData.prioridad} onValueChange={(v) => setFormData({ ...formData, prioridad: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="baja">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button 
              onClick={() => createMutation.mutate({ ...formData, solicitado_por: "usuario" })}
              disabled={!formData.titulo || !formData.descripcion}
            >
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoadmapCard({ item, onToggle, onDelete }) {
  const config = categoriaConfig[item.categoria];
  const IconComponent = config?.icon || Circle;
  const completada = item.estado === "completada";

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <button 
            onClick={() => onToggle(item)}
            className="mt-1 hover:opacity-70 transition-opacity"
          >
            {completada ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <Circle className="w-5 h-5 text-gray-400" />
            )}
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className={`font-medium text-gray-900 dark:text-white ${completada ? "line-through text-gray-500" : ""}`}>
                {item.titulo}
              </h3>
              <Badge className={config?.color}>
                <IconComponent className="w-3 h-3 mr-1" />
                {config?.label}
              </Badge>
              <div className={`w-2 h-2 rounded-full ${prioridadConfig[item.prioridad]?.color}`} title={`Prioridad ${item.prioridad}`} />
            </div>
            <p className={`text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap ${completada ? "line-through" : ""}`}>
              {item.descripcion}
            </p>
            {item.solicitado_por === "sistema" && (
              <Badge variant="outline" className="mt-2 text-xs">Sugerido por Base44</Badge>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => onDelete(item.id)}
            className="text-gray-400 hover:text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}